// app/javascript/controllers/billing_charge_form_controller.js
import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "serviceReferenceSelect",
    "descriptionInput",
    "serviceStartDate",
    "serviceEndDate",
    "taxRateHidden",
    "taxRateDisplay",
    "amountAfterTaxInput",
    "amountInput",
    "unitNameInput",
    "unitAmountInput",
    "unitQuantityInput",
    "amountCentsHidden",
    "amountAfterTaxCentsHidden",
  ];

  static values = {
    utilities: Object,
    services: Object,
    berths: Object,
  };

  connect() {
    this.amountInputHasDirectEdit = false;
    this.totalWithTaxHasDirectEdit = false;

    const initialTaxRate = parseFloat(this.taxRateHiddenTarget.value || 0);
    if (initialTaxRate > 0) {
      this.updateTaxRateDisplay(initialTaxRate);
    }

    // Initial calculation
    this.setTotalAmounts();
  }

  setFieldsFromServiceReference() {
    const selectedId = this.serviceReferenceSelectTarget.value;
    if (!selectedId) return;

    // Find selected service reference data from all possible sources
    const selected = this.findServiceReferenceById(selectedId);
    if (!selected) return;

    // Update the form fields based on the selection
    this.descriptionInputTarget.value = selected.description || "";
    this.taxRateHiddenTarget.value = selected.tax_rate || 0;
    this.updateTaxRateDisplay(selected.tax_rate || 0);

    // Set unit fields from service reference
    this.setUnitFieldsFromServiceReference(selected);

    // Update dates if service type is Berth
    if (selected.service_type === "Berth") {
      this.updateQuantityFromDates();
    }

    // Update calculations
    this.setTotalAmounts();
  }

  findServiceReferenceById(id) {
    // Look in all possible service references
    return this.utilitiesValue[id] || this.servicesValue[id] || this.berthsValue[id];
  }

  updateTaxRateDisplay(taxRate) {
    if (this.hasTaxRateDisplayTarget) {
      const taxType = "VAT"; // Default to VAT
      const taxPercent = (taxRate * 100).toFixed(1);
      const formattedPercent = taxPercent.endsWith(".0") ? taxPercent.slice(0, -2) : taxPercent;
      this.taxRateDisplayTarget.value = `${taxType} ${formattedPercent}%`;
    }
  }

  setUnitFieldsFromServiceReference(selected) {
    if (!selected) return;

    this.unitNameInputTarget.value = selected.unit_name || "service";
    this.unitAmountInputTarget.value = (selected.unit_price_cents || 0) / 100;
    this.unitQuantityInputTarget.value = 1;

    this.dispatchMoneyConverterEvent("unit-price");
  }

  // Calculate unit quantity based on date range (for berth charges)
  updateQuantityFromDates() {
    // Only auto-calculate for berth charges with day unit type
    if (this.unitNameInputTarget.value !== "day") {
      return;
    }

    // Get both dates
    const startDate = this.constructor.getDateFromPicker(this.serviceStartDateTarget);
    const endDate = this.constructor.getDateFromPicker(this.serviceEndDateTarget);

    if (!startDate || !endDate) {
      return;
    }

    // Calculate number of nights (days difference)
    const nights = this.constructor.calculateNightsBetween(startDate, endDate);

    if (nights > 0) {
      this.unitQuantityInputTarget.value = nights;
      this.setTotalAmounts();
    }
  }

  // Helper to get date from flatpickr input
  static getDateFromPicker(dateInput) {
    // Try to get from flatpickr if available
    // eslint-disable-next-line no-underscore-dangle
    if (dateInput._flatpickr && dateInput._flatpickr.selectedDates.length > 0) {
      // eslint-disable-next-line no-underscore-dangle
      return dateInput._flatpickr.selectedDates[0];
    }

    // Otherwise try to parse the input value
    const dateValue = dateInput.value;
    return dateValue ? new Date(dateValue) : null;
  }

  // Calculate nights between two dates
  static calculateNightsBetween(startDate, endDate) {
    if (!startDate || !endDate) {
      return 0;
    }

    // Clone dates to avoid modifying original objects
    const start = new Date(startDate.getTime());
    const end = new Date(endDate.getTime());

    // Set times to midnight to ignore time part
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    // Calculate days difference
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(0, diffDays);
  }

  setTotalAmount() {
    // Only calculate if we're not directly editing
    if (!this.amountInputHasDirectEdit && !this.totalWithTaxHasDirectEdit) {
      const unitPrice = parseFloat(this.unitAmountInputTarget.value || 0, 10);
      const quantity = parseFloat(this.unitQuantityInputTarget.value || 1, 10);

      // Calculate the amount from unit price × quantity
      const calculatedAmount = (unitPrice * quantity).toFixed(2);

      // Only update if the value is different to avoid loops
      if (this.amountInputTarget.value !== calculatedAmount) {
        this.amountInputTarget.value = calculatedAmount;
        this.dispatchMoneyConverterEvent("price-without-tax");
      }
    }

    // Reset the flags
    this.amountInputHasDirectEdit = false;
    this.totalWithTaxHasDirectEdit = false;
  }

  setTotalAmountAfterTax() {
    // Skip if we're directly editing the total with tax field
    if (this.totalWithTaxHasDirectEdit) {
      return;
    }

    const baseAmount = parseFloat(this.amountInputTarget.value) || 0;
    const taxRate = parseFloat(this.taxRateHiddenTarget.value || 0);

    if (Number.isNaN(baseAmount)) {
      this.amountAfterTaxInputTarget.value = "";
      this.amountAfterTaxCentsHiddenTarget.value = "";
    } else {
      const baseAmountCents = Math.round(baseAmount * 100) || 0;
      const amountAfterTaxCents = Math.round(baseAmountCents * (1 + taxRate)) || 0;
      this.amountAfterTaxInputTarget.value = (amountAfterTaxCents / 100).toFixed(2);
      this.amountAfterTaxCentsHiddenTarget.value = amountAfterTaxCents;

      // Update the money converter for total with tax
      this.dispatchMoneyConverterEvent("price-with-tax");
    }
  }

  // New method to handle changes to the Total Price with Tax
  setPriceFromTotalWithTax() {
    const totalWithTax = parseFloat(this.amountAfterTaxInputTarget.value || 0);
    const taxRate = parseFloat(this.taxRateHiddenTarget.value || 0);

    if (Number.isNaN(totalWithTax) || Number.isNaN(taxRate)) {
      return;
    }

    // Flag to prevent loops
    this.totalWithTaxHasDirectEdit = true;

    // Calculate the pre-tax amount
    const totalWithoutTax = totalWithTax / (1 + taxRate);
    this.amountInputTarget.value = totalWithoutTax.toFixed(2);

    // Update the hidden cents fields
    this.amountCentsHiddenTarget.value = Math.round(totalWithoutTax * 100);
    this.amountAfterTaxCentsHiddenTarget.value = Math.round(totalWithTax * 100);

    // Now update the unit price based on the new total
    const quantity = parseFloat(this.unitQuantityInputTarget.value || 1);
    if (quantity > 0) {
      const unitPrice = totalWithoutTax / quantity;
      this.unitAmountInputTarget.value = unitPrice.toFixed(2);

      // Trigger the money converter for unit price
      this.dispatchMoneyConverterEvent("unit-price");
    }

    // Trigger the money converter for total without tax
    this.dispatchMoneyConverterEvent("price-without-tax");
  }

  setTotalAmounts() {
    this.setTotalAmount();
    this.setTotalAmountAfterTax();
  }

  // Set unit price from amount (when amount field is edited directly)
  setUnitPriceFromAmount() {
    const amount = parseFloat(this.amountInputTarget.value || 0);
    const quantity = parseFloat(this.unitQuantityInputTarget.value || 1);

    if (Number.isNaN(amount) || Number.isNaN(quantity) || quantity <= 0) {
      return;
    }

    // Set flag to prevent loop in setTotalAmount
    this.amountInputHasDirectEdit = true;

    // Calculate unit price from amount and quantity
    const unitPrice = (amount / quantity).toFixed(2);
    this.unitAmountInputTarget.value = unitPrice;

    // Update the hidden cents fields
    this.dispatchMoneyConverterEvent("unit-price");

    // Update the total with tax
    this.setTotalAmountAfterTax();
  }

  dispatchMoneyConverterEvent(eventId) {
    const event = new CustomEvent(`money-converter:${eventId}-changed`, { bubbles: true });
    this.element.dispatchEvent(event);
  }
}
